/**
 * Reproductor del registro de eventos (ADR 0007): responde «¿qué estado había en el segundo t?».
 * La escena 3D solo dibuja ese estado; los traslados se interpolan sobre la ruta registrada.
 * Pausa, «siguiente evento», ×60–×600, ir al cierre y repetición son operaciones del reproductor,
 * no del motor: la velocidad visual nunca altera los resultados.
 */

import type { Evento } from '../dominio/ejecucion';
import type { Punto } from '../dominio/espacio';
import { longitud, puntoEnPolilinea } from '../espacial/geometria';

export type EstadoVisualUnidad =
  'cola-ingreso' | 'traslado' | 'actividad' | 'espera' | 'espera-externa';

export interface UnidadVisual {
  id: string;
  placa: string;
  tipoUnidad: string;
  x: number;
  z: number;
  /** Rumbo en radianes (convención de rotación del dominio). */
  rumbo: number;
  estado: EstadoVisualUnidad;
  etapaId: string | null;
  estacionId: string | null;
  listo: boolean;
  atrasada: boolean;
  horaPrometida: number | null;
}

export interface EstacionVisual {
  id: string;
  x: number;
  z: number;
  rotacion: number;
}

interface Tramo {
  desde: number;
  hasta: number;
  estado: EstadoVisualUnidad;
  etapaId: string | null;
  estacionId: string | null;
  ruta?: Punto[];
  largo?: number;
}

interface LineaUnidad {
  id: string;
  placa: string;
  tipoUnidad: string;
  llegada: number;
  salida: number;
  tramos: Tramo[];
  horaPrometida: number | null;
  listo: number | null;
}

export interface Reproductor {
  readonly inicio: number;
  readonly fin: number;
  /** Instantes con eventos, ordenados y sin repetir. */
  readonly instantes: readonly number[];
  estadoEn(t: number): UnidadVisual[];
  eventosEntre(a: number, b: number): Evento[];
  /** Próximo instante con eventos estrictamente posterior a t (o el fin). */
  siguienteEvento(t: number): number;
}

export interface OpcionesReproductor {
  eventos: readonly Evento[];
  estaciones: readonly EstacionVisual[];
  /** Polilíneas por «origen>destino»; si falta, el traslado se dibuja en línea recta. */
  rutas: Readonly<Record<string, Punto[]>>;
  inicio: number;
  fin: number;
  /** Separación entre autos en la cola de la calle (m). */
  separacionCola?: number;
}

function buscarTramo(tramos: readonly Tramo[], t: number): Tramo | null {
  let lo = 0;
  let hi = tramos.length - 1;
  let r: Tramo | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const tr = tramos[mid]!;
    if (tr.desde <= t) {
      r = tr;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return r;
}

export function crearReproductor(o: OpcionesReproductor): Reproductor {
  const estaciones = new Map(o.estaciones.map((e) => [e.id, e]));
  const lineas = new Map<string, LineaUnidad>();
  const instantes = new Set<number>();
  const separacion = o.separacionCola ?? 6;

  const linea = (id: string) => lineas.get(id);
  const cerrar = (l: LineaUnidad, t: number) => {
    const ultimo = l.tramos[l.tramos.length - 1];
    if (ultimo && ultimo.hasta === Infinity) ultimo.hasta = t;
  };
  const abrir = (l: LineaUnidad, t: number, tr: Omit<Tramo, 'desde' | 'hasta'>) => {
    cerrar(l, t);
    l.tramos.push({ ...tr, desde: t, hasta: Infinity });
  };

  for (const e of o.eventos) {
    if (e.unidadId) instantes.add(e.t);
    if (e.tipo === 'unidad.creada' && e.unidadId) {
      lineas.set(e.unidadId, {
        id: e.unidadId,
        placa: String(e.atributos?.placa ?? ''),
        tipoUnidad: String(e.atributos?.tipoUnidad ?? ''),
        llegada: Infinity,
        salida: Infinity,
        tramos: [],
        horaPrometida: null,
        listo: null,
      });
      continue;
    }
    const l = e.unidadId ? linea(e.unidadId) : undefined;
    if (!l) continue;
    switch (e.tipo) {
      case 'unidad.llegada':
        l.llegada = e.t;
        abrir(l, e.t, {
          estado: 'cola-ingreso',
          etapaId: null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'traslado.inicio': {
        const tr = e.traslado!;
        const desde = estaciones.get(tr.origen);
        const hasta = estaciones.get(tr.destino);
        const ruta =
          o.rutas[tr.rutaId] ??
          (desde && hasta
            ? [
                { x: desde.x, z: desde.z },
                { x: hasta.x, z: hasta.z },
              ]
            : []);
        abrir(l, e.t, {
          estado: 'traslado',
          etapaId: e.etapaId ?? null,
          estacionId: tr.destino,
          ruta,
          largo: longitud(ruta),
        });
        // El traslado dura lo que dice el registro (aunque no llegue un evento de fin en el horizonte).
        l.tramos[l.tramos.length - 1]!.hasta = e.t + tr.duracionS;
        break;
      }
      case 'traslado.fin': {
        const ultimo = l.tramos[l.tramos.length - 1];
        if (ultimo) ultimo.hasta = e.t;
        l.tramos.push({
          desde: e.t,
          hasta: Infinity,
          estado: 'espera',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      }
      case 'etapa.inicio':
      case 'etapa.reanudacion':
        abrir(l, e.t, {
          estado: e.categoria === 'espera-cliente' ? 'espera-externa' : 'actividad',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'etapa.interrupcion':
        abrir(l, e.t, {
          estado: 'espera-externa',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      case 'espera.inicio':
      case 'etapa.fin': {
        const enIngreso = e.ubicacion?.estacionId === 'ING';
        const ultimo = l.tramos[l.tramos.length - 1];
        if (ultimo?.estado === 'cola-ingreso') break;
        abrir(l, e.t, {
          estado: enIngreso ? 'cola-ingreso' : 'espera',
          etapaId: e.etapaId ?? null,
          estacionId: e.ubicacion?.estacionId ?? null,
        });
        break;
      }
      case 'unidad.hito':
        if (e.atributos?.hito === 'apertura-ot' && typeof e.atributos.horaPrometida === 'number') {
          l.horaPrometida = e.atributos.horaPrometida;
        }
        if (e.atributos?.hito === 'listo') l.listo = e.t;
        break;
      case 'unidad.salida':
        l.salida = e.t;
        cerrar(l, e.t);
        break;
      default:
        break;
    }
  }
  for (const l of lineas.values()) {
    for (const tr of l.tramos) if (tr.hasta === Infinity) tr.hasta = Math.max(o.fin, tr.desde);
  }
  const lista = [...lineas.values()].filter((l) => l.llegada !== Infinity);
  const ordenInstantes = [...instantes].sort((a, b) => a - b);
  const ordenEventos = [...o.eventos];
  const ingreso = estaciones.get('ING');

  // Dirección de la cola en la calle: opuesta a la primera ruta que sale del ingreso.
  let colaDir = { x: 0, z: 1 };
  const primera = Object.entries(o.rutas).find(([k]) => k.startsWith('ING>'))?.[1];
  if (primera && primera.length >= 2) {
    const a = primera[0]!;
    const b = primera[1]!;
    const L = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    colaDir = { x: -(b.x - a.x) / L, z: -(b.z - a.z) / L };
  }

  return {
    inicio: o.inicio,
    fin: o.fin,
    instantes: ordenInstantes,
    estadoEn(t: number): UnidadVisual[] {
      const r: UnidadVisual[] = [];
      const enCola: { l: LineaUnidad; tr: Tramo }[] = [];
      for (const l of lista) {
        if (t < l.llegada || t >= l.salida) continue;
        const tr = buscarTramo(l.tramos, t);
        if (!tr) continue;
        if (tr.estado === 'cola-ingreso') {
          enCola.push({ l, tr });
          continue;
        }
        let x = 0;
        let z = 0;
        let rumbo = 0;
        if (tr.estado === 'traslado' && tr.ruta && tr.ruta.length > 0) {
          const dur = Math.max(1, tr.hasta - tr.desde);
          const f = Math.min(1, Math.max(0, (t - tr.desde) / dur));
          const p = puntoEnPolilinea(tr.ruta, f * (tr.largo ?? 0));
          x = p.punto.x;
          z = p.punto.z;
          rumbo = p.rumbo;
        } else {
          const est = tr.estacionId ? estaciones.get(tr.estacionId) : undefined;
          if (est) {
            x = est.x;
            z = est.z;
            rumbo = est.rotacion;
          }
        }
        r.push(visual(l, tr, x, z, rumbo, t));
      }
      enCola.sort((a, b) => a.l.llegada - b.l.llegada || (a.l.id < b.l.id ? -1 : 1));
      enCola.forEach(({ l, tr }, k) => {
        const base = ingreso ?? { x: 0, z: 0 };
        const d = separacion * (k + 1);
        r.push(
          visual(
            l,
            tr,
            base.x + colaDir.x * d,
            base.z + colaDir.z * d,
            Math.atan2(-colaDir.x, -colaDir.z),
            t,
          ),
        );
      });
      return r;
    },
    eventosEntre(a: number, b: number): Evento[] {
      return ordenEventos.filter((e) => e.t >= a && e.t < b);
    },
    siguienteEvento(t: number): number {
      let lo = 0;
      let hi = ordenInstantes.length;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (ordenInstantes[mid]! <= t) lo = mid + 1;
        else hi = mid;
      }
      return lo < ordenInstantes.length ? Math.min(ordenInstantes[lo]!, o.fin) : o.fin;
    },
  };
}

function visual(
  l: LineaUnidad,
  tr: Tramo,
  x: number,
  z: number,
  rumbo: number,
  t: number,
): UnidadVisual {
  const listo = l.listo !== null && t >= l.listo;
  return {
    id: l.id,
    placa: l.placa,
    tipoUnidad: l.tipoUnidad,
    x,
    z,
    rumbo,
    estado: tr.estado,
    etapaId: tr.etapaId,
    estacionId: tr.estacionId,
    listo,
    atrasada: !listo && l.horaPrometida !== null && t > l.horaPrometida,
    horaPrometida: l.horaPrometida,
  };
}
